import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Card, Statistic, Button, List, Tag, Spin, Alert, Typography, Space } from 'antd';
import {
  ShopOutlined,
  ToolOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  EuroOutlined,
  PlusOutlined,
  EyeOutlined,
  CommentOutlined,
  GiftOutlined,
  TagOutlined,
  BankOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useStores } from '../../../stores';
import ImpactPartnerCard from '../../../components/partner/ImpactPartnerCard';
import './PartnerDashboard.scss';

const { Title, Text } = Typography;

const PartnerDashboard = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partnerDashboardStore } = useStores();
  const { stats, loading, error, profile, profileLoading } = partnerDashboardStore;

  useEffect(() => {
    partnerDashboardStore.fetchStats();
    partnerDashboardStore.fetchProfile();
  }, [partnerDashboardStore]);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      COMPLETED: 'green',
      CANCELLED: 'red',
      SHIPPED: 'purple',
      DELIVERED: 'green'
    };
    return colors[status] || 'default';
  };

  if (loading && !stats.inventory_count) {
    return (
      <div className="partner-dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="partner-dashboard">
      <div className="partner-dashboard-header">
        <Title level={2}>{t('partner.dashboard.title')}</Title>
        <Text type="secondary">{t('partner.dashboard.subtitle')}</Text>
      </div>

      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} className="stats-row">
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={t('partner.dashboard.products')}
              value={stats.inventory_count}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
            <Button
              type="link"
              size="small"
              onClick={() => navigate('/partner/inventory')}
              icon={<EyeOutlined />}
            >
              {t('partner.dashboard.products')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={t('partner.dashboard.activeServices')}
              value={stats.active_services}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Button
              type="link"
              size="small"
              onClick={() => navigate('/partner/services')}
              icon={<EyeOutlined />}
            >
              {t('partner.dashboard.activeServices')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={t('partner.dashboard.pendingAppointments')}
              value={stats.pending_appointments}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
            <Button
              type="link"
              size="small"
              onClick={() => navigate('/partner/appointments')}
              icon={<EyeOutlined />}
            >
              {t('partner.dashboard.viewAppointments')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={t('partner.dashboard.pendingOrders')}
              value={stats.pending_orders}
              prefix={<ShoppingOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
            <Button
              type="link"
              size="small"
              onClick={() => navigate('/partner/orders')}
              icon={<EyeOutlined />}
            >
              {t('partner.dashboard.viewOrders')}
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={t('partner.dashboard.monthlyRevenue')}
              value={parseFloat(stats.monthly_revenue)}
              precision={2}
              prefix={<EuroOutlined />}
              suffix="€"
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('partner.dashboard.thisMonth')}
            </Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8} lg={4}>
          <Card className="stat-card">
            <Statistic
              title={`🤝 ${t('partner.dashboard.ngoDonations')}`}
              value={parseFloat(stats.total_ngo_donations || 0)}
              precision={2}
              prefix={<GiftOutlined />}
              suffix="€"
              valueStyle={{ color: '#eb2f96' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('partner.dashboard.totalContributed')}
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Company Info & Impact Partner */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* Company Info Card */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BankOutlined />
                <span>{t('partner.dashboard.companyInfo')}</span>
              </Space>
            }
            loading={profileLoading}
            extra={
              <Button type="link" size="small" onClick={() => navigate('/partner/profile')}>
                {t('common.edit')}
              </Button>
            }
          >
            {profile ? (
              <div className="company-info">
                <div className="info-row">
                  <Text strong>{profile.store_name}</Text>
                  {profile.is_impact_partner && (
                    <Tag color="pink" style={{ marginLeft: 8 }}>
                      🤝 Impact Partner
                    </Tag>
                  )}
                </div>
                <div className="info-row">
                  <Text type="secondary">
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    {t('partner.profile.taxNumber')}: {profile.tax_number}
                  </Text>
                </div>
                <div className="info-row">
                  <Text type="secondary">
                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                    {profile.address}, {profile.city} {profile.postal_code}, {profile.country}
                  </Text>
                </div>
                {profile.user?.email && (
                  <div className="info-row">
                    <Text type="secondary">
                      <MailOutlined style={{ marginRight: 8 }} />
                      {profile.user.email}
                    </Text>
                  </div>
                )}
                {profile.user?.phone && (
                  <div className="info-row">
                    <Text type="secondary">
                      <PhoneOutlined style={{ marginRight: 8 }} />
                      {profile.user.phone}
                    </Text>
                  </div>
                )}
                {profile.collaboration_start_date && (
                  <div className="info-row">
                    <Text type="secondary">
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      {t('partner.dashboard.partnerSince')}: {new Date(profile.collaboration_start_date).toLocaleDateString()}
                    </Text>
                  </div>
                )}
                <div className="info-row" style={{ marginTop: 12 }}>
                  <Space>
                    <Tag color={profile.is_verified ? 'green' : 'orange'}>
                      {profile.is_verified ? t('common.verified') : t('common.pending')}
                    </Tag>
                    <Tag color="blue">
                      {t('common.price')}: {profile.platform_fee_percentage}%
                    </Tag>
                  </Space>
                </div>
              </div>
            ) : (
              <Text type="secondary">{t('common.loading')}</Text>
            )}
          </Card>
        </Col>

        {/* Impact Partner */}
        <Col xs={24} lg={12}>
          <ImpactPartnerCard />
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col span={24}>
          <Card title={t('partner.dashboard.quickActions')}>
            <Space size="middle" wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/partner/inventory/add')}
              >
                {t('partner.dashboard.addProduct')}
              </Button>
              <Button
                icon={<PlusOutlined />}
                onClick={() => navigate('/partner/services/add')}
              >
                {t('partner.dashboard.addService')}
              </Button>
              <Button
                icon={<CalendarOutlined />}
                onClick={() => navigate('/partner/appointments')}
              >
                {t('partner.dashboard.viewAppointments')}
              </Button>
              <Button
                icon={<ShoppingOutlined />}
                onClick={() => navigate('/partner/orders')}
              >
                {t('partner.dashboard.viewOrders')}
              </Button>
              <Button
                icon={<EuroOutlined />}
                onClick={() => navigate('/partner/revenue-report')}
              >
                {t('partner.dashboard.revenueReport')}
              </Button>
              <Button
                icon={<CommentOutlined />}
                onClick={() => navigate('/partner/reviews')}
              >
                {t('partner.dashboard.customerReviews')}
              </Button>
              <Button
                icon={<GiftOutlined />}
                onClick={() => navigate('/partner/packages')}
              >
                {t('partner.dashboard.itemPackages')}
              </Button>
              <Button
                icon={<TagOutlined />}
                onClick={() => navigate('/partner/coupons')}
              >
                {t('partner.dashboard.coupons')}
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={() => navigate('/partner/invoices')}
              >
                {t('partner.dashboard.invoices')}
              </Button>
              <Button
                icon={<TeamOutlined />}
                onClick={() => navigate('/partner/team')}
              >
                {t('partner.dashboard.team')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {/* Recent Appointments */}
        <Col xs={24} lg={12}>
          <Card
            title={t('partner.dashboard.recentAppointments')}
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => navigate('/partner/appointments')}
              >
                {t('partner.dashboard.viewAll')}
              </Button>
            }
          >
            {stats.recent_appointments.length === 0 ? (
              <Text type="secondary">{t('partner.dashboard.noAppointments')}</Text>
            ) : (
              <List
                dataSource={stats.recent_appointments}
                renderItem={(appointment) => (
                  <List.Item
                    key={appointment.id}
                    actions={[
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/partner/appointments/${appointment.id}`)}
                      >
                        {t('partner.dashboard.view')}
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{appointment.service_name_en}</span>
                          <Tag color={getStatusColor(appointment.status)}>
                            {appointment.status}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <div>
                            {t('partner.dashboard.customer')}: {appointment.customer_name} ({appointment.customer_email})
                          </div>
                          <div>
                            {t('partner.dashboard.date')}: {appointment.appointment_date} - {appointment.start_time}
                          </div>
                          {appointment.pet_details && (
                            <div>{t('partner.dashboard.animal')}: {appointment.pet_details.name}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>

        {/* Recent Orders */}
        <Col xs={24} lg={12}>
          <Card
            title={t('partner.dashboard.recentOrders')}
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => navigate('/partner/orders')}
              >
                {t('partner.dashboard.viewAll')}
              </Button>
            }
          >
            {stats.recent_orders.length === 0 ? (
              <Text type="secondary">{t('partner.dashboard.noOrders')}</Text>
            ) : (
              <List
                dataSource={stats.recent_orders}
                renderItem={(order) => (
                  <List.Item
                    key={order.id}
                    actions={[
                      <Button
                        type="link"
                        size="small"
                        onClick={() => navigate(`/partner/orders/${order.id}`)}
                      >
                        {t('partner.dashboard.view')}
                      </Button>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>#{order.id}</span>
                          <Tag color={getStatusColor(order.status)}>
                            {order.status}
                          </Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <div>
                            {t('partner.dashboard.customer')}: {order.user_first_name} {order.user_last_name} ({order.user_email})
                          </div>
                          <div>
                            {t('partner.dashboard.total')}: €{order.total_amount} | {t('partner.dashboard.items')}: {order.items.length}
                          </div>
                          <div>
                            {t('partner.dashboard.ordered')}: {new Date(order.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
});

export default PartnerDashboard;
